import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import HttpServices from 'src/services/httpService';
import { reportModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import { useToaster } from 'src/utils/toaster/toasterContext';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const CreateScheduledReportComponent = ({ handleReCallScheduleReport }) => {
  const { t: translate } = useTranslation();
  const sideBarWidth = 500;
  const { showToast } = useToaster();
  const navigate = useNavigate();
  const { handleCloseRightSidebar } = useRightSidebar();
  const currentBrandId = CommonHelper.GetCurrentBrandId();
  const currentBrandTime = CommonHelper.getBrandTimeZoneTime();
  const brandDetails = CommonHelper.GetBrandInfoFromLocalStorage('brand_details');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [format, setFormat] = useState(translate('report.pdf'));
  const [dateRange, setDateRange] = useState(translate('report.last_7_days'));
  const [groupBy, setGroupBy] = useState(translate('report.day'));
  const [frequency, setFrequency] = useState(translate('report.daily'));
  const [sendTime, setSendTime] = useState(currentBrandTime);
  const [height, setHeight] = useState(window.innerHeight - 150);
  const [isLoading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const is24HourFormat = CommonHelper.GetBrandInfoFromLocalStorage('is_24_hour_format');
  const [selectedDays, setSelectedDays] = useState({
    weekly_days: [0],
    monthly_days: [1],
  });
  const [predefinedReports, setPredefinedReports] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState('predefined');
  const [selectedReport, setSelectedReport] = useState({});

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = () => {
    const getProps = {
      url: reportModuleURL.fetchPredefinedAndCustomReports,
      successCallback: (data) => {
        setPredefinedReports(data.predefined_reports || []);
        setCustomReports(data.custom_reports || []);
        if (data.predefined_reports && data.predefined_reports.length > 0) {
          setSelectedReport({
            reportId: data.predefined_reports[0].predefined_report_details.report_id,
            subReportId: data.predefined_reports[0].predefined_report_details.report_id,
            connectedProfileId: data.predefined_reports[0].connected_profile_id,
            serviceName: data.predefined_reports[0].service_name,
            isCustomReport: false,
          });
        } else if (data.custom_reports && data.custom_reports.length > 0) {
          setSelectedReportType('custom');
          setSelectedReport({
            reportId: data.custom_reports[0].report_id,
            subReportId: 0,
            connectedProfileId: -1,
            serviceName: '',
            isCustomReport: true,
          });
        }
      },
      failureCallback: (message) => {
        showToast(translate('report.fetch_reports_failed'), 'e');
      },
    };
    HttpServices.Get(getProps);
  };

  const handleReportTypeChange = (event) => {
    const type = event.target.value;
    setSelectedReportType(type);
    setSelectedReport({});
    if (type === 'predefined' && predefinedReports.length > 0) {
      setSelectedReport({
        reportId: predefinedReports[0].predefined_report_details.report_id,
        subReportId: predefinedReports[0].predefined_report_details.report_id,
        connectedProfileId: predefinedReports[0].connected_profile_id,
        serviceName: predefinedReports[0].service_name,
        isCustomReport: false,
      });
    } else if (type === 'custom' && customReports.length > 0) {
      setSelectedReport({
        reportId: customReports[0].report_id,
        subReportId: -1,
        connectedProfileId: -1,
        serviceName: '',
        isCustomReport: true,
      });
    }
  };

  const handleReportChange = (event) => {
    const reportId = event.target.value;
    if (selectedReportType === 'predefined') {
      const report = predefinedReports.find(
        (r) => r.predefined_report_details.report_id === reportId
      );
      setSelectedReport({
        reportId: report.predefined_report_details.report_id,
        subReportId: report.predefined_report_details.report_id,
        connectedProfileId: report.connected_profile_id,
        serviceName: report.service_name,
        isCustomReport: false,
      });
    } else {
      const report = customReports.find((r) => r.report_id === reportId);
      setSelectedReport({
        reportId: report.report_id,
        subReportId: 0,
        connectedProfileId: -1,
        serviceName: '',
        isCustomReport: true,
      });
    }
  };

  const handleDayChange = (event, type) => {
    const value = event.target.value;
    setSelectedDays((prevSelectedDays) => ({
      ...prevSelectedDays,
      [type === 'weekly' ? 'weekly_days' : 'monthly_days']: value,
    }));
  };

  const handleCreateReport = (value) => {
    navigate(value);
    handleCloseRightSidebar();
  };

  const handleSend = () => {
    setEmailError('');

    if (!email) {
      setEmailError(translate('report.email_address_is_required'));
      showToast(translate('report.email_address_is_required'), 'e');
      return;
    }

    const emailList = email.split(',').map((e) => e.trim());
    const isValidEmail = emailList.every((emailItem) =>
      /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(emailItem)
    );

    if (!isValidEmail) {
      setEmailError(translate('report.please_enter_valid_email'));
      showToast(translate('report.please_enter_valid_email'), 'e');
      return;
    }

    if (!subject || subject.trim() === '') {
      showToast(translate('report.subject_is_required'), 'e');
      return;
    }

    if (!message || message.trim() === '') {
      showToast(translate('report.message_is_required'), 'e');
      return;
    }

    if (!selectedReport.reportId) {
      showToast(translate('report.select_report_required'), 'e');
      return;
    }

    setLoading(true);
    const formBody = {
      report_id: selectedReport.reportId,
      sub_report_id: selectedReport.subReportId,
      service_name: selectedReport.serviceName,
      connected_profile_id: selectedReport.connectedProfileId,
      recipient_email_addresses: emailList,
      subject: subject,
      email_body: message,
      date_range: dateRange,
      group_by: groupBy,
      schedule_type: frequency,
      scheduled_time: sendTime,
      is_custom_report: selectedReport.isCustomReport,
      report_format: format,
    };

    if (frequency === 'weekly') {
      formBody.schedule_type = 'weekly';
      formBody.weekly_days = selectedDays.weekly_days;
    } else if (frequency === 'monthly') {
      formBody.schedule_type = 'monthly';
      formBody.monthly_days = selectedDays.monthly_days;
    }

    const getProps = {
      url: reportModuleURL.addScheduledReport,
      body: formBody,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(getProps);
  };

  const successCallback = (data, message) => {
    showToast(message, 's');
    setLoading(false);
     if (handleReCallScheduleReport) {
      handleReCallScheduleReport();
    }
    handleCloseRightSidebar();
   
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    setLoading(false);
  };

  const handleScheduleTime = (newTime) => {
    if (newTime) {
      const convertedTime = CommonHelper.getBrandTimeZoneTime(newTime);
      setSendTime(convertedTime);
    }
  };

  return (
    <StyledComponent.StyledStack sx={{ width: sideBarWidth, p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {translate('scheduled_reports.scheduled_report')}
      </Typography>
      <Stack spacing={2}>
        {predefinedReports.length === 0 && customReports.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2, minHeight: 'calc(100vh - 150px)' }}>
            <Typography variant="body1" color="text.secondary">
              {translate('report.no_reports_available')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
             onClick={()=>{handleCreateReport("/report")}}
              sx={{ mt: 2 }}
            >
              {translate('report.create_report')}
            </Button>
          </Box>
        ) : (
          <>
            {/* Report Type Dropdown */}
            <Box>
              <FormControl fullWidth>
                <InputLabel>{translate('report.select_report_type')}</InputLabel>
                <Select
                  value={selectedReportType}
                  onChange={handleReportTypeChange}
                  label={translate('report.select_report_type')}
                >
                  <MenuItem value="predefined">{translate('report.predefined_reports')}</MenuItem>
                  <MenuItem value="custom">{translate('report.custom_reports')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Report Selection or Empty Message */}
            <Box>
              {selectedReportType === 'predefined' && predefinedReports.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={()=>{handleCreateReport("/report")}}
                    sx={{ mt: 2 }}
                  >
                    {translate('report.create_report')}
                  </Button>
                </Box>
              ) : selectedReportType === 'custom' && customReports.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={()=>{handleCreateReport("/custom-report")}}
                    sx={{ mt: 2 }}
                  >
                    {translate('report.create_report')}
                  </Button>
                </Box>
              ) : (
                <>
                  <FormControl fullWidth>
                    <InputLabel>{translate('report.select_report')}</InputLabel>
                    <Select
                      value={selectedReport.reportId || ''}
                      onChange={handleReportChange}
                      label={translate('report.select_report')}
                      disabled={
                        (selectedReportType === 'predefined' && !predefinedReports.length) ||
                        (selectedReportType === 'custom' && !customReports.length)
                      }
                    >
                      {selectedReportType === 'predefined' &&
                        predefinedReports.map((report) => (
                          <MenuItem
                            key={report.predefined_report_details.report_id}
                            value={report.predefined_report_details.report_id}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {CommonFunction.InitialReportAccountBadgeWithName(report, 30)}
                                <Typography fontWeight={500}>{report.profile_name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {report.predefined_report_details.report_display_name}
                                </Typography>
                              </Stack>
                            </Stack>
                          </MenuItem>
                        ))}
                      {selectedReportType === 'custom' &&
                        customReports.map((report) => (
                          <MenuItem key={report.report_id} value={report.report_id}>
                            {report.custom_report_name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>

            <TextField
              label={translate('report.recipient_email_address')}
              placeholder={translate('report.email_seperated_by_comma')}
              fullWidth
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
            />

            <TextField
              label={translate('report.subject')}
              multiline
              rows={2}
              fullWidth
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />

            <TextField
              label={translate('report.message')}
              multiline
              rows={5}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <Box>
              <Typography variant="body1">{translate('scheduled_reports.date_range')}</Typography>
              <Select value={dateRange} onChange={(e) => setDateRange(e.target.value)} fullWidth>
                <MenuItem value="last_7_days">{translate('scheduled_reports.last_7_days')}</MenuItem>
                <MenuItem value="last_30_days">{translate('scheduled_reports.last_30_days')}</MenuItem>
                <MenuItem value="last_90_days">{translate('scheduled_reports.last_90_days')}</MenuItem>
              </Select>
            </Box>

            <Box>
              <Typography variant="body1">{translate('scheduled_reports.group_data_by')}</Typography>
              <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} fullWidth>
                <MenuItem value="day">{translate('scheduled_reports.day')}</MenuItem>
                <MenuItem value="week">{translate('scheduled_reports.week')}</MenuItem>
                <MenuItem value="month">{translate('calendar.month')}</MenuItem>
              </Select>
            </Box>

            <Box>
              <Typography variant="body1">{translate('scheduled_reports.often_should_we_send_it')}</Typography>
              <Select value={frequency} onChange={(e) => setFrequency(e.target.value)} fullWidth>
                <MenuItem value="daily">{translate('scheduled_reports.daily')}</MenuItem>
                <MenuItem value="weekly">{translate('scheduled_reports.weekly')}</MenuItem>
                <MenuItem value="monthly">{translate('scheduled_reports.monthly')}</MenuItem>
              </Select>
            </Box>

            {(frequency === 'weekly' || frequency === 'monthly') && (
              <Box>
                <Typography variant="body1">
                  {frequency === 'weekly'
                    ? translate('scheduled_reports.select_days_of_the_week')
                    : translate('scheduled_reports.select_days_of_the_month')}
                </Typography>
                {frequency === 'weekly' && (
                  <FormControl fullWidth>
                    <InputLabel>{translate('scheduled_reports.select_days')}</InputLabel>
                    <Select
                      multiple
                      value={selectedDays.weekly_days}
                      onChange={(e) => handleDayChange(e, 'weekly')}
                      renderValue={(selected) => (
                        <Stack direction="row" spacing={1}>
                          {selected.map((day) => (
                            <Chip
                              key={day}
                              label={
                                [
                                  translate('scheduled_reports.sunday'),
                                  translate('scheduled_reports.monday'),
                                  translate('scheduled_reports.tuesday'),
                                  translate('scheduled_reports.wednesday'),
                                  translate('scheduled_reports.thursday'),
                                  translate('scheduled_reports.friday'),
                                  translate('scheduled_reports.saturday'),
                                ][day]
                              }
                            />
                          ))}
                        </Stack>
                      )}
                    >
                      {[
                        translate('scheduled_reports.sunday'),
                        translate('scheduled_reports.monday'),
                        translate('scheduled_reports.tuesday'),
                        translate('scheduled_reports.wednesday'),
                        translate('scheduled_reports.thursday'),
                        translate('scheduled_reports.friday'),
                        translate('scheduled_reports.saturday'),
                      ].map((day, i) => (
                        <MenuItem key={day} value={i}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {frequency === 'monthly' && (
                  <FormControl fullWidth>
                    <InputLabel>{translate('scheduled_reports.select_days')}</InputLabel>
                    <Select
                      multiple
                      value={selectedDays.monthly_days}
                      onChange={(e) => handleDayChange(e, 'monthly')}
                      renderValue={(selected) => (
                        <Stack direction="row" spacing={1}>
                          {selected.map((day) => (
                            <Chip key={day} label={`${translate('scheduled_reports.day')} ${day}`} />
                          ))}
                        </Stack>
                      )}
                    >
                      {[...Array(31).keys()].map((day) => (
                        <MenuItem key={day} value={day + 1}>
                          {`${translate('scheduled_reports.day')} ${day + 1}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Typography variant="body1">{translate('scheduled_reports.choose_send_time')}</Typography>
              <TimePicker
                value={dayjs(sendTime)}
                onChange={handleScheduleTime}
                ampm={!is24HourFormat}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>

            <Box>
              <Typography variant="body1" gutterBottom>
                {translate('report.report_format')}
              </Typography>
              <Select value={format} onChange={(e) => setFormat(e.target.value)} fullWidth>
                <MenuItem value="PDF">{translate('report.pdf')}</MenuItem>
              </Select>
            </Box>

            <Divider />

            <Stack direction="row" spacing={2}>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={handleSend}
                loading={isLoading}
                disabled={
                  (selectedReportType === 'predefined' && !predefinedReports.length) ||
                  (selectedReportType === 'custom' && !customReports.length)
                }
              >
                {translate('report.send')}
              </LoadingButton>
            </Stack>
          </>
        )}
      </Stack>
    </StyledComponent.StyledStack>
  );
};

export default CreateScheduledReportComponent;